<script>
	// imports
	import model from "../../stores/modelStore";
	import lookups from "../../stores/lookupStore";
	import { projectSettings } from "../../config";
	import { unformatDate } from "../../helpers/dateHelpers";
	import { isValid } from "../../validation";
	import { registerPageComplete } from "../../tealium";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import SaveAndClose from "../../components/SaveAndClose.svelte";
	import ClassActionPhoneNumber from "../../components/ClassActionPhoneNumber.svelte";
	import ClassActionEmailAddress from "../../components/ClassActionEmailAddress.svelte";

	// props
	export let pageName = null;
	export let persistUrl = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	let dateApproximateEndUse = null;
	let datesValid = true;
	let buttons = null;

	let appliedToBodyAreaIdList = [];
	let showOtherAppliedToBodyArea = false;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const thisYear = new Date().getFullYear();

	const useFrequencyGenitalAreaOtherId = $lookups.useFrequencyGenitalArea.filter((l) => l.lookupName === "Other")[0]
		.id;
	const useFrequencyOtherAreaOtherId = $lookups.useFrequencyOtherArea.filter((l) => l.lookupName === "Other")[0].id;
	const purchaseFrequencyOtherId = $lookups.purchaseFrequency.filter((l) => l.lookupName === "Other/Unknown")[0].id;
	const appliedToBodyAreasOtherId = $lookups.appliedBodyAreaType.filter((l) => l.lookupName === "Other")[0].id;

	const mapAppliedToBodyAreasValues = (arrayOfInts) => {
		if (arrayOfInts) {
			var array = arrayOfInts.map(function (value) {
				return { appliedToBodyAreaTypeId: value };
			});
			return array;
		}
		return [];
	};

	const handleSubmit = async (e) => {
		buttons.blockNext();

		//console.log("TalcUsage submit", $model);

		$model.talcumPowderUse.appliedToBodyAreas = mapAppliedToBodyAreasValues(appliedToBodyAreaIdList);

		let errors = [...isValid(), ...customValidator()];

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
			} else {
				registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);
				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	const customValidator = () => {
		return datesValid ? [] : [dateApproximateEndUse];
	};

	const handleRadioGroupChange = (e) => {
		switch (e.target.name) {
			case "useFrequencyGenitalAreaId":
				$model.talcumPowderUse.useFrequencyGenitalAreaOther = null;
				break;
			case "useFrequencyOtherAreaId":
				$model.talcumPowderUse.useFrequencyOtherAreaOther = null;
				break;
			case "purchaseFrequencyId":
				$model.talcumPowderUse.purchaseFrequencyOther = null;
				break;
		}
	};

	const handleDateChange = (e) => {
		if (e.target.id == "dateApproximateStartUse") {
			if (!$model.talcumPowderUse.dateApproximateEndUse || !e.target.value) {
				datesValid = true;
			} else {
				let d1 = e.target.value;
				let d2 = $model.talcumPowderUse.dateApproximateEndUse;

				datesValid = d1 <= d2;
			}
		} else if (e.target.id == "dateApproximateEndUse") {
			if (!$model.talcumPowderUse.dateApproximateStartUse || !e.target.value) {
				datesValid = true;
			} else {
				let d1 = $model.talcumPowderUse.dateApproximateStartUse;
				let d2 = e.target.value;

				datesValid = d1 <= d2;
			}
		}
	};

	const handleAppliedToBodyAreasChange = (e) => {
		//console.log("handleAppliedToBodyAreasChange", e, appliedToBodyAreaIdList);
	};

	const initialize = () => {
		if ($model.talcumPowderUse && $model.talcumPowderUse.appliedToBodyAreas) {
			appliedToBodyAreaIdList = $model.talcumPowderUse.appliedToBodyAreas.map(
				(item) => item.appliedToBodyAreaTypeId
			);
		}
	};

	initialize();

	$: {
		if (appliedToBodyAreaIdList && appliedToBodyAreaIdList.length > 0) {
			let found = false;
			for (let i = 0; i < appliedToBodyAreaIdList.length; i++) {
				if (appliedToBodyAreaIdList[i] === appliedToBodyAreasOtherId) {
					found = true;
					break;
				}
			}
			showOtherAppliedToBodyArea = found;
		} else {
			showOtherAppliedToBodyArea = false;
		}
	}
</script>

<PageSection>
	<Form {disabled} id="form-medical-history" on:submit={handleSubmit}>
		<FormRow class="confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Talcum Powder Use</h1>
				<p>
					If you are unable to complete any part of this section of the form, we are here to assist you.
					Please contact us on <ClassActionPhoneNumber /> or via email to <ClassActionEmailAddress />.
				</p>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<p>
				<strong>
					If you are completing this form on behalf of someone else please respond to the remaining questions
					with respect to the person who was diagnosed with ovarian cancer following the use of Johnson &amp;
					Johnson's Talcum Powder.
				</strong>
			</p>

			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="usedJJTalc"
						label="Have you ever used Johnson &amp; Johnson's Talcum Powder?"
						bind:value={$model.talcumPowderUse.usedJJTalc}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.talcumPowderUse.usedJJTalc === "Yes"}
				<div bind:this={dateApproximateEndUse}>
					<FormRow>
						<FormColumn cols="6">
							<NumberInput
								required
								{disabled}
								id="dateApproximateStartUse"
								label="Approximate year of first use"
								bind:value={$model.talcumPowderUse.yearApproximateStartUse}
								minvalue={1900}
								maxvalue={new Date().getFullYear()}
								errorMessage="The value must be between 1900 and {thisYear}"
								on:change={handleDateChange}
							/>
						</FormColumn>
						<FormColumn cols="6">
							<div class:invalid={!datesValid}>
								<NumberInput
									required
									{disabled}
									id="dateApproximateEndUse"
									label="Approximate year of last use"
									bind:value={$model.talcumPowderUse.yearApproximateEndUse}
									minvalue={1900}
									maxvalue={new Date().getFullYear()}
									errorMessage="The value must be between 1900 and {thisYear}"
									on:change={handleDateChange}
								/>
								{#if !datesValid}
									<div class="error-message">
										The end year must be before or the same as the start year
									</div>
								{/if}
							</div>
						</FormColumn>
					</FormRow>
				</div>

				<FormRow>
					<FormColumn>
						<RadioGroup
							required
							{disabled}
							name="useFrequencyGenitalAreaId"
							cols="4"
							on:change={handleRadioGroupChange}
							label="How frequently did you apply Johnson &amp; Johnson's Talcum Powder in the above period?"
							optionList={$lookups.useFrequencyGenitalArea}
							labelProperty="lookupName"
							valueProperty="id"
							bind:value={$model.talcumPowderUse.useFrequencyGenitalAreaId}
						/>
					</FormColumn>
				</FormRow>

				{#if $model.talcumPowderUse.useFrequencyGenitalAreaId === useFrequencyGenitalAreaOtherId}
					<FormRow>
						<FormColumn>
							<TextInput
								label="Please specify how often you applied Johnson &amp; Johnson's Talcum Powder"
								required
								{disabled}
								bind:value={$model.talcumPowderUse.useFrequencyGenitalAreaOther}
								id="useFrequencyGenitalAreaOther"
							/>
						</FormColumn>
					</FormRow>
				{/if}

				<FormRow>
					<FormColumn cols="6">
						<NumberInput
							required
							{disabled}
							id="ageFirstTalcUse"
							label="How old were you when you first used Johnson &amp; Johnson's Talcum Powder?"
							bind:value={$model.talcumPowderUse.ageFirstTalcUse}
							minvalue={0}
							maxvalue={100}
							errorMessage="Please provide a valid age"
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn>
						<YesNoGroup
							required
							{disabled}
							name="useInGenitalArea"
							label="Did you apply Johnson &amp; Johnson's Talcum Powder to your perineal/genital area? (For clarity, the perineal/genital area encompasses the bottom region of your pelvic cavity, between your thighs, and your vulva/vagina and anus)"
							bind:value={$model.talcumPowderUse.useInGenitalArea}
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn>
						<CheckboxButtonList
							{disabled}
							name="applyToOtherAreaOfBody"
							label="Did you apply Johnson &amp; Johnson's Talcum Powder to any other area of your body?"
							bind:selected={appliedToBodyAreaIdList}
							optionList={$lookups.appliedBodyAreaType}
							validator="checkboxRequired"
							validatorValue="1"
							validatorMessage="At least one selection is required"
							validatorTarget=".apply-error"
							on:change={handleAppliedToBodyAreasChange}
						/>
						<div class="apply-error"></div>
					</FormColumn>
				</FormRow>

				{#if showOtherAppliedToBodyArea}
					<FormRow>
						<FormColumn cols="6">
							<TextInput
								placeholder="Please specify"
								required
								{disabled}
								bind:value={$model.talcumPowderUse.otherAppliedToBodyArea}
								id="otherAppliedToBodyArea"
							/>
						</FormColumn>
					</FormRow>
				{/if}
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="useOtherProductInGenitalArea"
						label="Have you ever used any other cosmetic powder product(s) in your genital area?"
						bind:value={$model.talcumPowderUse.useOtherProductInGenitalArea}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.talcumPowderUse.useOtherProductInGenitalArea === "Yes"}
				<FormRow>
					<FormColumn>
						<TextArea
							label="Please identify name of product(s) and approximate dates of use"
							required
							{disabled}
							bind:value={$model.talcumPowderUse.otherProductName}
							id="otherProductName"
						/>
					</FormColumn>
				</FormRow>
			{/if}
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<SaveAndClose>Save and Close</SaveAndClose>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"} />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>
